import { customElement, observable, resolve } from 'aurelia';
import { IRouter, route, type Params } from '@aurelia/router';
import { I18N } from '@aurelia/i18n';
import template from './person-face-ratings.html';
import {
  formatPersonName,
  getPerson,
  getPersonMarkerCropUrl,
  listPersonFaceMarkers,
  updatePersonMarkerRating,
} from '../services/api.service';
import type { FoldChangeOperation } from '../components/media-lightbox';
import type {
  FaceType,
  FaceMarkerSize,
  ListPersonFaceMarkersOptions,
  MediaFoldGroup,
  MediaItem,
  Person,
  PersonFaceMarker,
  PersonMarker,
  UpdatePersonMarkerRatingDto,
} from '../services/api.types';
import {
  FaceMarkerSize as FaceMarkerSizeValue,
  FaceType as FaceTypeValue,
} from '../services/api.types';
import { FACE_TYPE_OPTIONS } from '../services/face-type-options';

type RatingStatus = 'all' | 'anyUnrated' | 'sharpnessUnrated' | 'faceTypeUnrated' | 'generalUnrated';
type RatingUndoChange =
  | { field: 'sharpnessRating'; previousValue: number; nextValue: number }
  | { field: 'faceType'; previousValue: FaceType; nextValue: FaceType }
  | { field: 'generalRating'; previousValue: number; nextValue: number };

interface RatingUndoEntry {
  id: number;
  markerId: string;
  personId: string;
  change: RatingUndoChange;
}

@route({ path: ['persons/face-ratings', 'persons/:id/face-ratings'], title: 'Face ratings' })
@customElement({ name: 'person-face-ratings', template })
export class PersonFaceRatings {
  private readonly i18n: I18N = resolve(I18N);
  private readonly router: IRouter = resolve(IRouter);
  private _scrollObserver: IntersectionObserver | null = null;
  private _loadGeneration = 0;
  private _cursorId: string | null = null;
  private readonly _ratingQueues = new Map<string, Promise<void>>();
  private readonly _ratingRevisions = new Map<string, number>();
  private _nextUndoId = 0;
  private _lightboxCursorId: string | null = null;
  private _lightboxMarkerIds: string[] = [];

  public personId: string | null = null;
  public person: Person | null = null;
  public markers: PersonFaceMarker[] = [];
  public totalCount = 0;
  public visibleMarkerStart = 0;
  public visibleMarkerEnd = 0;
  public hasMore = false;
  public isLoading = false;
  public isLoadingMore = false;
  public isLoadingMoreLightbox = false;
  public error = '';
  public saveErrors: Record<string, string> = {};
  public savingMarkers: Record<string, boolean> = {};
  public undoHistory: RatingUndoEntry[] = [];
  public isUndoing = false;

  public scrollContainer: HTMLElement | null = null;
  public scrollSentinel: HTMLElement | null = null;
  private _visibleRangeResizeObserver: ResizeObserver | null = null;
  private _visibleRangeFrame: number | null = null;
  private readonly _visibleRangeScrollHandler = (): void => this.scheduleVisibleRangeUpdate();
  private readonly _visibleRangeResizeHandler = (): void => this.scheduleVisibleRangeUpdate();

  public ratingStatus: RatingStatus = 'all';
  @observable public sortOption = 'capturedat:desc';
  public faceTypeFilter: FaceType | 'all' = 'all';
  public markerSizeFilter: FaceMarkerSize | 'all' = 'all';
  public minSharpnessRating = 0;
  public maxSharpnessRating = 0;
  public minGeneralRating = 0;

  public lightboxItems: MediaItem[] = [];
  public lightboxStartIndex = 0;
  public lightboxExhausted = false;
  public faceRatingGridEl: HTMLElement | null = null;
  private _lightboxOriginMarkerId: string | null = null;

  public readonly sharpnessOptions = Array.from({ length: 10 }, (_, index) => index + 1);
  public readonly generalRatingOptions = [1, 2, 3, 4, 5];
  public readonly faceTypeOptions = FACE_TYPE_OPTIONS;
  public readonly markerSizeOptions: { value: FaceMarkerSize; labelKey: string }[] = [
    { value: FaceMarkerSizeValue.Micro, labelKey: 'faceRatings.sizes.micro' },
    { value: FaceMarkerSizeValue.Mini, labelKey: 'faceRatings.sizes.mini' },
    { value: FaceMarkerSizeValue.Small, labelKey: 'faceRatings.sizes.small' },
    { value: FaceMarkerSizeValue.Medium, labelKey: 'faceRatings.sizes.medium' },
    { value: FaceMarkerSizeValue.Large, labelKey: 'faceRatings.sizes.large' },
    { value: FaceMarkerSizeValue.Huge, labelKey: 'faceRatings.sizes.huge' },
  ];

  public get personName(): string {
    return this.person ? formatPersonName(this.person) : this.i18n.tr('faceRatings.allPersons');
  }

  public get lightboxHasMore(): boolean {
    return !this.lightboxExhausted;
  }

  public get canUndoLastRating(): boolean {
    return this.undoHistory.length > 0
      && !this.isUndoing
      && !this.isLoading
      && !Object.values(this.savingMarkers).some(Boolean);
  }

  public async loading(params: Params): Promise<void> {
    this.personId = params.id ?? null;
    this.undoHistory = [];

    try {
      this.person = this.personId ? await getPerson(this.personId) : null;
      await this.reload();
    } catch (error: unknown) {
      this.error = error instanceof Error ? error.message : String(error);
    }
  }

  public attached(): void {
    this.scrollContainer?.addEventListener('scroll', this._visibleRangeScrollHandler, { passive: true });
    window.addEventListener('resize', this._visibleRangeResizeHandler);
    if (this.scrollContainer) {
      this._visibleRangeResizeObserver = new ResizeObserver(() => this.scheduleVisibleRangeUpdate());
      this._visibleRangeResizeObserver.observe(this.scrollContainer);
    }
    if (this.scrollSentinel && this.scrollContainer) {
      this._scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) void this.loadMore();
        },
        { root: this.scrollContainer, threshold: 0.1 },
      );
      this._scrollObserver.observe(this.scrollSentinel);
    }
    this.scheduleVisibleRangeUpdate();
  }

  public detaching(): void {
    this.scrollContainer?.removeEventListener('scroll', this._visibleRangeScrollHandler);
    window.removeEventListener('resize', this._visibleRangeResizeHandler);
    this._visibleRangeResizeObserver?.disconnect();
    this._visibleRangeResizeObserver = null;
    this._scrollObserver?.disconnect();
    this._scrollObserver = null;
    if (this._visibleRangeFrame !== null) {
      cancelAnimationFrame(this._visibleRangeFrame);
      this._visibleRangeFrame = null;
    }
  }

  public async reload(): Promise<void> {
    const generation = ++this._loadGeneration;
    this.isLoading = true;
    this.isLoadingMore = false;
    this.isLoadingMoreLightbox = false;
    this.error = '';
    this._cursorId = null;
    this.markers = [];
    this.totalCount = 0;
    this.hasMore = false;
    this.closeLightbox();

    try {
      const result = await listPersonFaceMarkers(this.personId, this.requestOptions());
      if (generation !== this._loadGeneration) return;
      this.markers = result.markers;
      this.totalCount = result.totalCount;
      this.hasMore = result.hasMore;
      this._cursorId = result.markers.at(-1)?.marker.id ?? null;
    } catch (error: unknown) {
      if (generation === this._loadGeneration)
        this.error = error instanceof Error ? error.message : String(error);
    } finally {
      if (generation === this._loadGeneration) {
        this.isLoading = false;
        this.scheduleVisibleRangeUpdate();
      }
    }
  }

  public filtersChanged(): void {
    void this.reload();
  }

  public sortOptionChanged(): void {
    this.filtersChanged();
  }

  public clearFilters(): void {
    this.ratingStatus = 'all';
    this.faceTypeFilter = 'all';
    this.markerSizeFilter = 'all';
    this.minSharpnessRating = 0;
    this.maxSharpnessRating = 0;
    this.minGeneralRating = 0;
    void this.reload();
  }

  public async goBack(): Promise<void> {
    await this.router.load('persons');
  }

  public cropUrl(item: PersonFaceMarker): string {
    const previewVersion = `${item.marker.lastUpdatedAt}:${item.marker.personMarkerPreviewSavedAt ?? ''}`;
    return getPersonMarkerCropUrl(item.marker.personId, item.marker.id, previewVersion);
  }

  public mediaLabel(item: PersonFaceMarker): string {
    return item.mediaTitle;
  }

  public markerPersonName(item: PersonFaceMarker): string {
    const name = formatPersonName({
      firstName: item.marker.personFirstName,
      lastName: item.marker.personLastName,
      nickName: item.marker.personNickName ?? '',
    });
    return name || this.i18n.tr('faceRatings.unknownPerson');
  }

  public mediaTooltip(item: PersonFaceMarker): string {
    return item.mediaItem?.description?.trim() || this.i18n.tr('faceRatings.openImage');
  }

  public onCropKeyDown(item: PersonFaceMarker, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.openLightbox(item);
  }

  public openLightbox(item: PersonFaceMarker): void {
    const entries = this.getLightboxEntries(this.markers);
    const startIndex = entries.findIndex(entry => entry.markerId === item.marker.id);
    if (startIndex < 0) return;

    this.lightboxItems = entries.map(entry => entry.mediaItem);
    this._lightboxMarkerIds = entries.map(entry => entry.markerId);
    this._lightboxCursorId = this._lightboxMarkerIds.at(-1) ?? null;
    this._lightboxOriginMarkerId = item.marker.id;
    this.lightboxStartIndex = startIndex;
    this.lightboxExhausted = !this.hasMore;
  }

  public closeLightbox(lastItem?: MediaItem | null): void {
    const lastMarkerIndex = lastItem
      ? this.lightboxItems.findIndex(item => item.id === lastItem.id)
      : -1;
    const markerId = lastMarkerIndex >= 0
      ? this._lightboxMarkerIds[lastMarkerIndex]
      : this._lightboxOriginMarkerId;
    this.lightboxItems = [];
    this._lightboxMarkerIds = [];
    this._lightboxCursorId = null;
    this.lightboxStartIndex = 0;
    this.lightboxExhausted = false;
    this._lightboxOriginMarkerId = null;
    if (markerId) {
      requestAnimationFrame(() => {
        const target = Array.from(
          this.faceRatingGridEl?.querySelectorAll<HTMLElement>('[data-marker-id]') ?? [],
        ).find(element => element.dataset['markerId'] === markerId);
        target?.focus({ preventScroll: true });
      });
    }
  }

  public async requestMoreForLightbox(): Promise<void> {
    if (this.isLoadingMoreLightbox || !this.lightboxHasMore || !this._lightboxCursorId) return;

    const generation = this._loadGeneration;
    const afterId = this._lightboxCursorId;
    this.isLoadingMoreLightbox = true;
    try {
      const result = await listPersonFaceMarkers(this.personId, {
        ...this.requestOptions(),
        afterId,
      });
      if (generation !== this._loadGeneration) return;

      const existingIds = new Set(this._lightboxMarkerIds);
      const newMarkers = result.markers.filter(item => !existingIds.has(item.marker.id));
      const newEntries = this.getLightboxEntries(newMarkers);
      this.lightboxItems = [...this.lightboxItems, ...newEntries.map(entry => entry.mediaItem)];
      this._lightboxMarkerIds = [...this._lightboxMarkerIds, ...newEntries.map(entry => entry.markerId)];
      this._lightboxCursorId = result.markers.at(-1)?.marker.id ?? afterId;
      this.totalCount = result.totalCount;
      this.lightboxExhausted = !result.hasMore || newMarkers.length === 0;
    } catch (error: unknown) {
      if (generation === this._loadGeneration)
        this.error = error instanceof Error ? error.message : String(error);
    } finally {
      if (generation === this._loadGeneration)
        this.isLoadingMoreLightbox = false;
    }
  }

  public onLightboxSaved(item: MediaItem): void {
    this.markers = this.markers.map(marker => marker.mediaItem?.id === item.id
      ? {
        ...marker,
        mediaFileName: item.fileName,
        mediaTitle: item.title,
        capturedAt: item.capturedAt,
        mediaItem: item,
      }
      : marker);
    this.lightboxItems = this.lightboxItems.map(existing => existing.id === item.id ? item : existing);
  }

  public onLightboxFoldChanged(
    group: MediaFoldGroup | null,
    updatedItem: MediaItem | null,
    operation: FoldChangeOperation,
  ): void {
    const replacements = new Map<string, MediaItem>();
    if (group) {
      if (operation === 'group') {
        for (const item of group.items) {
          replacements.set(
            item.id,
            item.id === updatedItem?.id
              ? updatedItem
              : { ...item, foldedIntoMediaItemId: null, foldedImageCount: 0 },
          );
        }
      } else if (operation === 'item') {
        replacements.set(group.representative.id, {
          ...group.representative,
          foldedImageCount: Math.max(0, group.representative.foldedImageCount - 1),
        });
        if (updatedItem) replacements.set(updatedItem.id, updatedItem);
      } else {
        for (const item of group.items) replacements.set(item.id, item);
      }
    } else if (updatedItem) {
      replacements.set(updatedItem.id, updatedItem);
    }

    if (replacements.size === 0) return;
    this.markers = this.markers.map(entry => {
      const mediaItem = entry.mediaItem;
      const replacement = mediaItem ? replacements.get(mediaItem.id) : undefined;
      return replacement
        ? {
          ...entry,
          mediaFileName: replacement.fileName,
          mediaTitle: replacement.title,
          capturedAt: replacement.capturedAt,
          mediaItem: replacement,
        }
        : entry;
    });
    this.lightboxItems = this.lightboxItems.map(item => replacements.get(item.id) ?? item);
  }

  public onLightboxDeleted(_id: string): void {
    this.closeLightbox();
    void this.reload();
  }

  public onLightboxPersonDataChanged(): void {
    void this.reload();
  }

  public isSaving(markerId: string): boolean {
    return this.savingMarkers[markerId] === true;
  }

  public setSharpness(item: PersonFaceMarker, rating: number): void {
    this.enqueueRating(item, { sharpnessRating: rating });
  }

  public setFaceType(item: PersonFaceMarker, faceType: FaceType): void {
    this.enqueueRating(item, { faceType });
  }

  public setGeneralRating(item: PersonFaceMarker, rating: number): void {
    this.enqueueRating(item, { generalRating: rating });
  }

  public async undoLastRating(): Promise<void> {
    if (!this.canUndoLastRating) return;

    const entry = this.undoHistory.at(-1);
    if (!entry) return;

    this.undoHistory = this.undoHistory.slice(0, -1);
    this.isUndoing = true;
    this.error = '';
    const inverseDto = this.ratingDtoForValue(entry.change, 'previousValue');
    const currentItem = this.markers.find(item => item.marker.id === entry.markerId);
    if (currentItem) this.applyOptimisticRating(entry.markerId, inverseDto);

    try {
      const updated = await updatePersonMarkerRating(entry.personId, entry.markerId, inverseDto);
      if (this.markers.some(item => item.marker.id === entry.markerId)) {
        this.applyServerMarker(updated);
      } else {
        await this.reload();
      }
    } catch (error: unknown) {
      if (currentItem) {
        this.applyOptimisticRating(
          entry.markerId,
          this.ratingDtoForValue(entry.change, 'nextValue'),
        );
      }
      this.undoHistory = [...this.undoHistory, entry].slice(-10);
      this.error = error instanceof Error
        ? error.message
        : this.i18n.tr('faceRatings.undoError');
    } finally {
      this.isUndoing = false;
    }
  }

  public isFaceTypeSelected(item: PersonFaceMarker, faceType: FaceType): boolean {
    return item.marker.faceType === faceType;
  }

  public isSharpnessSelected(item: PersonFaceMarker, rating: number): boolean {
    return item.marker.sharpnessRating === rating;
  }

  public isGeneralRatingSelected(item: PersonFaceMarker, rating: number): boolean {
    return item.marker.generalRating >= rating;
  }

  private getLightboxEntries(markers: PersonFaceMarker[]): { markerId: string; mediaItem: MediaItem }[] {
    return markers.flatMap(item => item.mediaItem
      ? [{ markerId: item.marker.id, mediaItem: item.mediaItem }]
      : []);
  }

  private requestOptions(): ListPersonFaceMarkersOptions {
    const [sortBy, direction] = this.sortOption.split(':');
    const options: ListPersonFaceMarkersOptions = {
      pageSize: 24,
      sortBy,
      sortDescending: direction !== 'asc',
      anyUnratedOnly: this.ratingStatus === 'anyUnrated',
      sharpnessUnratedOnly: this.ratingStatus === 'sharpnessUnrated',
      generalRatingUnratedOnly: this.ratingStatus === 'generalUnrated',
      minSharpnessRating: this.minSharpnessRating > 0 ? this.minSharpnessRating : undefined,
      maxSharpnessRating: this.maxSharpnessRating > 0 ? this.maxSharpnessRating : undefined,
      minGeneralRating: this.minGeneralRating > 0 ? this.minGeneralRating : undefined,
      markerSize: this.markerSizeFilter === 'all' ? undefined : this.markerSizeFilter,
    };

    if (this.ratingStatus === 'faceTypeUnrated')
      options.faceType = FaceTypeValue.Unrated;
    else if (this.faceTypeFilter !== 'all')
      options.faceType = this.faceTypeFilter;

    return options;
  }

  private async loadMore(): Promise<void> {
    if (this.isLoading || this.isLoadingMore || !this.hasMore || !this._cursorId) return;

    const generation = this._loadGeneration;
    const afterId = this._cursorId;
    this.isLoadingMore = true;
    try {
      const result = await listPersonFaceMarkers(this.personId, {
        ...this.requestOptions(),
        afterId,
      });
      if (generation !== this._loadGeneration) return;

      const existingIds = new Set(this.markers.map(item => item.marker.id));
      const newMarkers = result.markers.filter(item => !existingIds.has(item.marker.id));
      this.markers = [...this.markers, ...newMarkers];
      this.totalCount = result.totalCount;
      this._cursorId = result.markers.at(-1)?.marker.id ?? this._cursorId;
      this.hasMore = result.hasMore && newMarkers.length > 0;
      this.scheduleVisibleRangeUpdate();
    } catch (error: unknown) {
      if (generation === this._loadGeneration)
        this.error = error instanceof Error ? error.message : String(error);
    } finally {
      if (generation === this._loadGeneration) {
        this.isLoadingMore = false;
        this.scheduleVisibleRangeUpdate();
      }
    }
  }

  private enqueueRating(item: PersonFaceMarker, dto: UpdatePersonMarkerRatingDto): void {
    const undoEntry = this.createUndoEntry(item, dto);
    if (!undoEntry) return;

    const markerId = item.marker.id;
    const revision = (this._ratingRevisions.get(markerId) ?? 0) + 1;
    this._ratingRevisions.set(markerId, revision);
    this.undoHistory = [...this.undoHistory, undoEntry].slice(-10);
    this.applyOptimisticRating(markerId, dto);
    this.saveErrors = { ...this.saveErrors };
    delete this.saveErrors[markerId];
    this.savingMarkers = { ...this.savingMarkers, [markerId]: true };

    const previous = this._ratingQueues.get(markerId) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(async () => {
      try {
        const updated = await updatePersonMarkerRating(item.marker.personId, markerId, dto);
        if (this._ratingRevisions.get(markerId) === revision)
          this.applyServerMarker(updated);
      } catch (error: unknown) {
        this.removeUndoEntry(undoEntry.id);
        if (this._ratingRevisions.get(markerId) === revision) {
          this.saveErrors = {
            ...this.saveErrors,
            [markerId]: error instanceof Error ? error.message : String(error),
          };
        }
      }
    });

    this._ratingQueues.set(markerId, next);
    void next.finally(() => {
      if (this._ratingQueues.get(markerId) === next) {
        this._ratingQueues.delete(markerId);
        this._ratingRevisions.delete(markerId);
        this.savingMarkers = { ...this.savingMarkers, [markerId]: false };
      }
    });
  }

  private createUndoEntry(item: PersonFaceMarker, dto: UpdatePersonMarkerRatingDto): RatingUndoEntry | null {
    if (dto.sharpnessRating !== undefined && dto.sharpnessRating !== item.marker.sharpnessRating) {
      return {
        id: ++this._nextUndoId,
        markerId: item.marker.id,
        personId: item.marker.personId,
        change: {
          field: 'sharpnessRating',
          previousValue: item.marker.sharpnessRating,
          nextValue: dto.sharpnessRating,
        },
      };
    }
    if (dto.faceType !== undefined && dto.faceType !== item.marker.faceType) {
      return {
        id: ++this._nextUndoId,
        markerId: item.marker.id,
        personId: item.marker.personId,
        change: {
          field: 'faceType',
          previousValue: item.marker.faceType,
          nextValue: dto.faceType,
        },
      };
    }
    if (dto.generalRating !== undefined && dto.generalRating !== item.marker.generalRating) {
      return {
        id: ++this._nextUndoId,
        markerId: item.marker.id,
        personId: item.marker.personId,
        change: {
          field: 'generalRating',
          previousValue: item.marker.generalRating,
          nextValue: dto.generalRating,
        },
      };
    }
    return null;
  }

  private removeUndoEntry(entryId: number): void {
    this.undoHistory = this.undoHistory.filter(entry => entry.id !== entryId);
  }

  private ratingDtoForValue(
    change: RatingUndoChange,
    value: 'previousValue' | 'nextValue',
  ): UpdatePersonMarkerRatingDto {
    switch (change.field) {
      case 'sharpnessRating':
        return { sharpnessRating: change[value] };
      case 'faceType':
        return { faceType: change[value] };
      case 'generalRating':
        return { generalRating: change[value] };
    }
  }

  private applyOptimisticRating(markerId: string, dto: UpdatePersonMarkerRatingDto): void {
    const item = this.markers.find(candidate => candidate.marker.id === markerId);
    if (!item) return;
    const marker = { ...item.marker };
    if (dto.sharpnessRating !== undefined) marker.sharpnessRating = dto.sharpnessRating;
    if (dto.faceType !== undefined) marker.faceType = dto.faceType;
    if (dto.generalRating !== undefined) marker.generalRating = dto.generalRating;
    this.markers = this.markers.map(candidate =>
      candidate.marker.id === markerId ? { ...candidate, marker } : candidate);
  }

  private applyServerMarker(updated: PersonMarker): void {
    const item = this.markers.find(candidate => candidate.marker.id === updated.id);
    if (!item) return;

    if (!this.matchesFilters(updated)) {
      this.markers = this.markers.filter(candidate => candidate.marker.id !== updated.id);
      this.totalCount = Math.max(0, this.totalCount - 1);
      this.markers = this.markers.map(candidate => candidate.position > item.position
        ? { ...candidate, position: candidate.position - 1 }
        : candidate);
      this.removeLightboxMarker(updated.id);
      this.scheduleVisibleRangeUpdate();
      return;
    }

    this.markers = this.markers.map(candidate =>
      candidate.marker.id === updated.id ? { ...candidate, marker: updated } : candidate);
  }

  private matchesFilters(marker: PersonMarker): boolean {
    if (this.ratingStatus === 'anyUnrated'
      && marker.sharpnessRating >= 0
      && marker.faceType !== FaceTypeValue.Unrated
      && marker.generalRating >= 0)
      return false;
    if (this.ratingStatus === 'sharpnessUnrated' && marker.sharpnessRating >= 0)
      return false;
    if (this.ratingStatus === 'faceTypeUnrated' && marker.faceType !== FaceTypeValue.Unrated)
      return false;
    if (this.ratingStatus === 'generalUnrated' && marker.generalRating >= 0)
      return false;
    if (this.minSharpnessRating > 0 && marker.sharpnessRating < this.minSharpnessRating)
      return false;
    if (this.maxSharpnessRating > 0
      && (marker.sharpnessRating < 0 || marker.sharpnessRating > this.maxSharpnessRating))
      return false;
    if (this.minGeneralRating > 0 && marker.generalRating < this.minGeneralRating)
      return false;
    return this.ratingStatus === 'faceTypeUnrated'
      || this.faceTypeFilter === 'all'
      || marker.faceType === this.faceTypeFilter;
  }

  private removeLightboxMarker(markerId: string): void {
    const index = this._lightboxMarkerIds.indexOf(markerId);
    if (index < 0) return;

    this._lightboxMarkerIds = this._lightboxMarkerIds.filter((_, candidateIndex) => candidateIndex !== index);
    this.lightboxItems = this.lightboxItems.filter((_, candidateIndex) => candidateIndex !== index);
    if (this.lightboxItems.length === 0)
      this.closeLightbox();
  }

  private scheduleVisibleRangeUpdate(): void {
    if (this._visibleRangeFrame !== null) return;
    this._visibleRangeFrame = requestAnimationFrame(() => {
      this._visibleRangeFrame = null;
      this.updateVisibleRange();
    });
  }

  private updateVisibleRange(): void {
    const container = this.scrollContainer;
    const cards = Array.from(
      this.faceRatingGridEl?.querySelectorAll<HTMLElement>('.face-rating-card') ?? [],
    );
    if (this.isLoading || !container || cards.length === 0 || this.totalCount === 0) {
      this.visibleMarkerStart = 0;
      this.visibleMarkerEnd = 0;
      return;
    }

    const positionByMarkerId = new Map(this.markers.map(item => [item.marker.id, item.position]));
    const containerRect = container.getBoundingClientRect();
    const viewportTop = Math.max(containerRect.top, 0);
    const viewportBottom = Math.min(containerRect.bottom, window.innerHeight);
    let firstVisible = Number.MAX_SAFE_INTEGER;
    let lastVisible = 0;

    for (const card of cards) {
      const markerId = card.dataset['ratingMarkerId'];
      const position = markerId ? positionByMarkerId.get(markerId) : undefined;
      if (position === undefined) continue;
      const rect = card.getBoundingClientRect();
      if (rect.bottom > viewportTop && rect.top < viewportBottom) {
        firstVisible = Math.min(firstVisible, position);
        lastVisible = Math.max(lastVisible, position);
      }
    }

    if (firstVisible === Number.MAX_SAFE_INTEGER) {
      this.visibleMarkerStart = 0;
      this.visibleMarkerEnd = 0;
      return;
    }

    this.visibleMarkerStart = firstVisible;
    this.visibleMarkerEnd = lastVisible;
  }
}
